import { ObserversModule } from '@angular/cdk/observers';
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AtlasModule } from '@galaxy/atlas';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  CONVERSATION_IMAGE_SERVICE_TOKEN,
  ConversationCoreModule,
} from '@galaxy/conversation/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { InboxButtonComponent, InboxComponent } from '@galaxy/conversation/inbox';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConversationUIModule } from '@galaxy/conversation/ui';
import { LexiconModule } from '@galaxy/lexicon';
import { VendorService } from '@galaxy/marketplace-apps/v1';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyI18NModule } from '@vendasta/galaxy/i18n';
import { GalaxyNavModule } from '@vendasta/galaxy/nav';
import { GalaxyDefaultProviderOverrides } from '@vendasta/galaxy/provider-default-overrides';
import { ProductAnalyticsModule } from '@vendasta/product-analytics';
import { StoreModule } from '@vendasta/store';
import { VaNavItemListModule, VaNavItemModule, VaNavModule } from '@vendasta/uikit';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthService } from './core/auth/auth.service';
import { CoreModule } from './core/core.module';
import { ImageService } from './core/image.service';
import { PageContentModule } from './core/page-content/page-content.module';
import { ConversationVendorCenterService } from './features/inbox/conversation-vendor-center.service';
import { imageServiceFactory } from './features/inbox/external-providers';
import { INBOX_CONFIG_FOR_CONVERSATION } from './features/inbox/providers';
import { OmniSearchModule } from './features/omni-search/omni-search.module';
import { ToolbarModule } from './features/toolbar/toolbar.module';
import { SharedModule } from './shared/shared.module';
import { PARTNER_ID, VENDOR_ID } from './tokens';
import { VendorCrmModule } from './features/crm/vendor-crm.module';
import { GoogleMapsModule } from '@angular/google-maps';

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatTooltipModule,
    VaNavModule,
    VaNavItemModule,
    VaNavItemListModule,
    CoreModule,
    SharedModule,
    AppRoutingModule,
    GalaxyI18NModule,
    TranslateModule,
    LexiconModule.forRoot(),
    HttpClientModule,
    AtlasModule,
    ToolbarModule,
    ProductAnalyticsModule,
    ConversationUIModule,
    InboxButtonComponent,
    InboxComponent,
    ObserversModule,
    StoreModule,
    GalaxyNavModule,
    GalaxyAlertModule,
    ConversationCoreModule.forRoot({ config: INBOX_CONFIG_FOR_CONVERSATION }),
    VendorCrmModule,
    OmniSearchModule,
    PageContentModule,
    GoogleMapsModule,
  ],
  providers: [
    ...GalaxyDefaultProviderOverrides,
    {
      provide: PARTNER_ID,
      useFactory: partnerIdFactory,
      deps: [AuthService],
    },
    // this string injection token is needed for some libraries (StoreModule) that depend on it
    // ideally each library should expose their own config to allow consumers to safely provide dependencies that can be relied on
    { provide: 'PARTNER_ID', useExisting: PARTNER_ID },
    {
      provide: VENDOR_ID,
      useFactory: vendorIdFactory,
      deps: [PARTNER_ID, VendorService],
    },

    {
      provide: CONVERSATION_IMAGE_SERVICE_TOKEN,
      useFactory: imageServiceFactory,
      deps: [ImageService],
    },
    { provide: CONVERSATION_HOST_APP_INTERFACE_TOKEN, useClass: ConversationVendorCenterService },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

function partnerIdFactory(authService: AuthService): Observable<string> {
  return authService.partnerId$;
}

function vendorIdFactory(partnerId$: Observable<string>, vendorService: VendorService): Observable<string> {
  return partnerId$.pipe(
    switchMap((partnerId) => {
      return vendorService.getVendorIdByPartnerId(partnerId).pipe(
        filter((vendorId) => !!vendorId),
        distinctUntilChanged(),
      );
    }),
  );
}
